import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import toast from 'react-hot-toast';
import { buildSchedule, todayLocal } from '../utils/schedule';
import SchedulePicker from './SchedulePicker';
import VideoRequestForm from './VideoRequestForm';
import VideoWizard from './VideoWizard';

const STATUS_LABEL = {
  requested: 'Waiting for the worker',
  preparing: 'Preparing',
  needs_review: 'Ready to review',
  ready: 'Ready to assign',
  failed: 'Failed',
};

function fmtTime(s) {
  s = Math.max(0, Math.round(Number(s) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h ? `${h}:${String(m).padStart(2, '0')}` : `${m}`) + `:${String(sec).padStart(2, '0')}`;
}

function fmtEta(seconds) {
  if (seconds >= 3600) return `${Math.round(seconds / 360) / 10} hours`;
  if (seconds >= 90) return `${Math.round(seconds / 60)} minutes`;
  return 'a minute';
}

function WorkerBadge({ worker }) {
  if (!worker) return null;
  return worker.online
    ? <span className="worker-badge online" role="status">Worker online</span>
    : <span className="worker-badge offline" role="status">
        Worker offline: new videos will wait until the VideoQuiz worker on the devPi is running.
      </span>;
}

function progressText(v) {
  const p = v.progress;
  if (!p) return null;
  const pct = Math.round((p.progress || 0) * 100);
  return `${p.stage || 'working'}${p.progress != null ? ` (${pct}%)` : ''}${p.eta_seconds ? `, about ${fmtEta(p.eta_seconds)} left` : ''}`;
}

function AssignForm({ video, onDone, onCancel }) {
  const [students, setStudents] = useState(null);
  const [picked, setPicked] = useState([]);
  const [sched, setSched] = useState({ from: 1, to: 1, perDay: 1, date: todayLocal(), days: [1, 2, 3, 4, 5] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getUsers().then(us => setStudents(us.filter(u => u.role === 'student'))).catch(err => toast.error(err.message));
  }, []);

  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const schedule = buildSchedule({ from: sched.from, to: sched.to, perDay: sched.perDay, days: sched.days, startDate: sched.date });

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.assignVideo({ video_id: video.id, student_ids: picked, schedule });
      toast.success(r.created.length ? `Scheduled ${r.created.length} assignment${r.created.length === 1 ? '' : 's'}` : 'Already assigned');
      onDone();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  }

  return (
    <form className="video-form" onSubmit={submit} style={{ marginTop: 12, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
      <fieldset style={{ border: 0 }}>
        <legend style={{ fontWeight: 600 }}>Assign to</legend>
        {students === null && <span className="video-meta">Loading students…</span>}
        {students && students.length === 0 && <span className="video-meta">No students yet. Add them on the Admin tab.</span>}
        {students && students.map(s => (
          <label key={s.id} style={{ display: 'inline-flex', gap: 6, marginRight: 16, fontWeight: 400 }}>
            <input type="checkbox" checked={picked.includes(s.id)} onChange={() => toggle(s.id)} /> {s.name}
          </label>
        ))}
      </fieldset>
      <SchedulePicker idPrefix={`as-${video.id}`} parts={video.parts} value={sched} onChange={patch => setSched(v => ({ ...v, ...patch }))} />
      <div className="video-actions" style={{ marginTop: 10 }}>
        <button className="btn-primary" type="submit" disabled={busy || picked.length === 0 || schedule.length === 0}>{busy ? 'Scheduling…' : 'Schedule'}</button>
        <button className="btn-secondary" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function AssignmentResults({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');

  useEffect(() => {
    api.getUsers().then(us => setStudents(us.filter(u => u.role === 'student'))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const from = new Date(Date.now() - 30 * 86400000).toLocaleDateString('en-CA');
    try { setRows(await api.getVideoResults({ from, ...(studentId ? { student_id: studentId } : {}) })); }
    catch (err) { toast.error(err.message); }
  }, [studentId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  async function waive(a, p) {
    if (!window.confirm(`Let ${a.student_name} take the part ${p.idx} quiz without finishing the video?`)) return;
    try { await api.waiveVideoWatch(a.id, p.idx); toast.success('They can take the quiz now'); load(); }
    catch (err) { toast.error(err.message); }
  }

  async function waiveAll(a) {
    if (!window.confirm(`Let ${a.student_name} take every quiz in this assignment (parts ${a.part_from}-${a.part_to}) without watching the video here?`)) return;
    try { await api.waiveAllVideoWatch(a.id); toast.success('All quizzes in this assignment are open'); load(); }
    catch (err) { toast.error(err.message); }
  }

  async function remove(a) {
    if (!window.confirm(`Remove this assignment for ${a.student_name}? Their quiz results for it are deleted too.`)) return;
    try { await api.deleteVideoAssignment(a.id); toast.success('Removed'); load(); }
    catch (err) { toast.error(err.message); }
  }

  if (rows === null) return null;
  return (
    <section className="video-card" aria-labelledby="assign-results-h">
      <div className="video-row">
        <h3 id="assign-results-h">Assignments and results <span className="video-meta">(last 30 days)</span></h3>
        {students.length > 0 && (
          <label className="video-meta">Student{' '}
            <select value={studentId} onChange={e => setStudentId(e.target.value)} aria-label="Show results for">
              <option value="">Everyone</option>
              {students.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
        )}
      </div>
      {rows.length === 0 && <p className="video-meta">Nothing assigned yet.</p>}
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(a => (
          <li key={a.id} className="q-item">
            <div className="video-row">
              <div>
                <strong>{a.student_name}</strong> · {a.video_title || 'Video'} · {a.part_from === a.part_to ? `part ${a.part_from}` : `parts ${a.part_from}–${a.part_to}`}
                <div className="video-meta">Due {a.assigned_date}</div>
              </div>
              <div className="video-actions">
                {a.completed_at
                  ? <span className="status-chip ready">Done {new Date(a.completed_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  : <span className="status-chip">{a.parts.some(p => p.attempts.length) ? 'In progress' : 'Not started'}</span>}
                {a.video_source !== 'rumble' && !a.completed_at && (
                  <button className="btn-small" onClick={() => waiveAll(a)}>Waive watching for all parts</button>
                )}
                <button className="btn-small btn-danger-link" style={{ background: 'none' }} onClick={() => remove(a)}>Remove</button>
              </div>
            </div>
            <ul className="video-meta" style={{ listStyle: 'none', marginTop: 4 }}>
              {a.parts.map(p => (
                <li key={p.idx}>
                  Part {p.idx}{p.title ? ` (${p.title})` : ''}:{' '}
                  {p.attempts.length === 0 ? 'not attempted'
                    : <><strong>first try {p.first_score}/{p.first_total}</strong>
                        {p.attempts.length > 1 ? `, ${p.attempts.length} tries` : ''}{p.complete ? ', perfect ✓' : ', not perfect yet'}</>}
                  {a.video_source !== 'rumble' && p.length_s > 0 && (
                    <span> · watched {fmtTime(p.watched_s)} of {fmtTime(p.length_s)}{p.watch_waived ? ' (quiz allowed by you)' : ''}</span>
                  )}
                  {a.video_source !== 'rumble' && !p.complete && p.attempts.length === 0 && !p.watch_waived && p.watched_s < 0.85 * p.length_s && (
                    <button className="btn-small" style={{ marginLeft: 8 }} onClick={() => waive(a, p)}>Let them take the quiz</button>
                  )}
                  {p.first_missed.length > 0 && (
                    <div>Missed on the first try: {p.first_missed.map(m => `“${m.prompt}”`).join('; ')}</div>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VideoCard({ v, onReview, onChanged, onAssigned }) {
  const [assigning, setAssigning] = useState(false);
  const waitingToRetry = v.status === 'requested' && v.retry_after && new Date(v.retry_after) > new Date();

  async function retry() {
    try { await api.retryVideo(v.id); toast.success('Back in the queue'); onChanged(); }
    catch (err) { toast.error(err.message); }
  }
  async function remove() {
    if (!window.confirm(`Delete "${v.title || v.url}" and its questions?`)) return;
    try { await api.deleteVideo(v.id); toast.success('Deleted'); onChanged(); }
    catch (err) { toast.error(err.message); }
  }

  return (
    <li className="video-card">
      <div className="video-row">
        <div>
          <h4>{v.title || v.url}</h4>
          <span className={`status-chip ${v.status}`}>{STATUS_LABEL[v.status] || v.status}</span>{' '}
          <span className="video-meta">
            {v.source === 'rumble' ? 'Rumble' : 'YouTube'}
            {v.duration_s ? ` · ${fmtTime(v.duration_s)}` : ''}
            {v.parts ? ` · ${v.parts} parts · ${v.questions_included}/${v.questions_total} questions included` : ''}
            {v.part_count && !v.parts ? ` · ${v.part_count} parts requested` : ''}
            {v.has_transcript ? ' · your transcript' : ''}
          </span>
        </div>
        <div className="video-actions">
          {v.status === 'ready' &&
            <button className="btn-small" onClick={() => setAssigning(a => !a)} aria-expanded={assigning}>Assign…</button>}
          {(v.status === 'needs_review' || v.status === 'ready') &&
            <button className="btn-small" onClick={() => onReview(v.id)}>{v.status === 'ready' ? 'Review again' : 'Review questions'}</button>}
          {(v.status === 'failed' || waitingToRetry) &&
            <button className="btn-small" onClick={retry}>Try again now</button>}
          <button className="btn-small btn-danger-link" style={{ background: 'none' }} onClick={remove}>Delete</button>
        </div>
      </div>
      {v.status === 'preparing' && <div className="video-meta" role="status">{progressText(v) || 'Starting…'}</div>}
      {waitingToRetry && <div className="video-error">{v.error} Next attempt after {new Date(v.retry_after).toLocaleTimeString()}.</div>}
      {v.status === 'failed' && <div className="video-error">{v.error}</div>}
      {assigning && <AssignForm video={v} onCancel={() => setAssigning(false)} onDone={() => { setAssigning(false); onAssigned(); }} />}
    </li>
  );
}

function ClipPreview({ video, q }) {
  const [open, setOpen] = useState(false);
  const start = Math.floor(q.clip_start_s), end = Math.ceil(q.clip_end_s);
  if (video.source !== 'youtube') {
    return <span className="video-meta">Clip at {fmtTime(q.clip_start_s)}–{fmtTime(q.clip_end_s)}</span>;
  }
  return (
    <>
      <button type="button" className="btn-small" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {open ? 'Hide clip' : `Watch clip (${fmtTime(q.clip_start_s)}–${fmtTime(q.clip_end_s)})`}
      </button>
      {open && (
        <div className="clip-frame">
          <iframe title={`Clip for: ${q.prompt}`} allow="autoplay; encrypted-media" allowFullScreen
                  src={`https://www.youtube-nocookie.com/embed/${video.source_key}?start=${start}&end=${end}&autoplay=1&rel=0`} />
        </div>
      )}
    </>
  );
}

function QuestionItem({ video, q, stat, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  function startEdit() {
    setDraft({ prompt: q.prompt, choices: [...q.choices], answer_index: q.answer_index, explanation: q.explanation || '' });
    setEditing(true);
  }

  async function toggleIncluded() {
    const next = !q.included;
    onChange({ id: q.id, included: next });   // show it straight away; undo if the save fails
    try { onChange(await api.updateVideoQuestion(video.id, q.id, { included: next })); }
    catch (err) { onChange({ id: q.id, included: !next }); toast.error(err.message); }
  }

  async function save() {
    try {
      onChange(await api.updateVideoQuestion(video.id, q.id, draft));
      setEditing(false);
      toast.success('Saved');
    } catch (err) { toast.error(err.message); }
  }

  if (editing) {
    return (
      <li className="q-item q-edit">
        <label htmlFor={`qp-${q.id}`}>Question</label>
        <textarea id={`qp-${q.id}`} rows="2" value={draft.prompt} onChange={e => setDraft({ ...draft, prompt: e.target.value })} />
        <fieldset style={{ border: 0 }}>
          <legend>Choices (select the correct one)</legend>
          {draft.choices.map((c, i) => (
            <div className="choice-edit" key={i}>
              <input type="radio" name={`ans-${q.id}`} checked={draft.answer_index === i} aria-label={`Choice ${i + 1} is correct`}
                     onChange={() => setDraft({ ...draft, answer_index: i })} />
              <input type="text" value={c} aria-label={`Choice ${i + 1}`}
                     onChange={e => setDraft({ ...draft, choices: draft.choices.map((x, j) => (j === i ? e.target.value : x)) })} />
            </div>
          ))}
        </fieldset>
        <label htmlFor={`qe-${q.id}`}>Explanation (only you see this)</label>
        <textarea id={`qe-${q.id}`} rows="2" value={draft.explanation} onChange={e => setDraft({ ...draft, explanation: e.target.value })} />
        <div className="video-actions">
          <button type="button" className="btn-primary" onClick={save}>Save</button>
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </li>
    );
  }

  return (
    <li className={`q-item ${q.included ? '' : 'excluded'}`}>
      <label>
        <input type="checkbox" checked={q.included} onChange={toggleIncluded} /> Include in quiz
      </label>
      {stat && stat.students_first_try > 0 && (
        <span className={`q-stat ${stat.first_try_missed * 2 >= stat.students_first_try ? 'hard' : ''}`}>
          Missed on the first try by {stat.first_try_missed} of {stat.students_first_try} student{stat.students_first_try === 1 ? '' : 's'}
        </span>
      )}
      <div className="q-prompt">{q.prompt}</div>
      <ul className="q-choices">
        {q.choices.map((c, i) => (
          <li key={i} className={i === q.answer_index ? 'correct' : ''}>
            {String.fromCharCode(65 + i)}. {c}{i === q.answer_index ? ' ✓' : ''}
          </li>
        ))}
      </ul>
      {q.explanation && <div className="q-explain"><strong>Why:</strong> {q.explanation}</div>}
      {q.quote && <div className="q-quote">“{q.quote}”</div>}
      <div className="video-actions">
        <ClipPreview video={video} q={q} />
        <button type="button" className="btn-small" onClick={startEdit}>Edit</button>
      </div>
    </li>
  );
}

function VideoReview({ id, onBack, onChanged }) {
  const [video, setVideo] = useState(null);
  const [emptyParts, setEmptyParts] = useState([]);
  const [stats, setStats] = useState({});

  const load = useCallback(async () => {
    try { setVideo(await api.getVideo(id)); } catch (err) { toast.error(err.message); }
    api.getVideoStats(id).then(setStats).catch(() => {});   // optional extra; the review works without it
  }, [id]);
  useEffect(() => { load(); }, [load]);

  function replaceQuestion(partId, updated) {
    setVideo(v => ({
      ...v,
      parts: v.parts.map(p => (p.id !== partId ? p : { ...p, questions: p.questions.map(q => (q.id === updated.id ? { ...q, ...updated } : q)) })),
    }));
    setEmptyParts([]);
  }

  async function markReady() {
    try {
      await api.markVideoReady(id);
      toast.success('Marked ready. It can now be assigned.');
      onChanged();
      load();
    } catch (err) {
      toast.error(err.message);
      try {
        // the server names the empty parts in its message; re-derive them from what's on screen
        setEmptyParts(video.parts.filter(p => !p.questions.some(q => q.included)).map(p => p.idx));
      } catch { /* ignore */ }
    }
  }

  if (!video) return <div className="loading">Loading…</div>;
  const included = video.parts.reduce((n, p) => n + p.questions.filter(q => q.included).length, 0);
  const total = video.parts.reduce((n, p) => n + p.questions.length, 0);

  return (
    <div className="video-page">
      <div className="video-card review-bar">
        <div>
          <button type="button" className="btn-link" style={{ color: '#1e3a5f' }} onClick={onBack}>← All videos</button>
          <h3>{video.title || video.url}</h3>
          <div className="video-meta">
            <span className={`status-chip ${video.status}`}>{STATUS_LABEL[video.status]}</span>{' '}
            {video.parts.length} parts · {included}/{total} questions included
          </div>
        </div>
        <button className="btn-primary" onClick={markReady} disabled={video.status !== 'needs_review' && video.status !== 'ready'}>
          {video.status === 'ready' ? 'Marked ready ✓' : 'Mark ready to assign'}
        </button>
      </div>
      {video.parts.map(p => (
        <section key={p.id} className={`part-block ${emptyParts.includes(p.idx) ? 'empty' : ''}`} aria-labelledby={`part-${p.id}`}>
          <h4 id={`part-${p.id}`}>Part {p.idx}: {p.title || 'Untitled'} <span className="video-meta">({fmtTime(p.start_s)}–{fmtTime(p.end_s)})</span></h4>
          {p.questions.length === 0 && <p className="video-error">No questions were written for this part (too little speech?). Every part needs at least one before the video can be assigned. Ask for fewer parts, or delete the video and request it again.</p>}
          {emptyParts.includes(p.idx) && p.questions.length > 0 && <p className="video-error">Every part needs at least one included question.</p>}
          <ul style={{ listStyle: 'none' }}>
            {p.questions.map(q => <QuestionItem key={q.id} video={video} q={q} stat={stats[q.id]} onChange={u => replaceQuestion(p.id, u)} />)}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default function VideoAdmin() {
  const [videos, setVideos] = useState(null);
  const [worker, setWorker] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [resultsKey, setResultsKey] = useState(0);
  const [wizard, setWizard] = useState(false);

  const load = useCallback(async () => {
    try {
      const [vs, w] = await Promise.all([api.getVideos(), api.getWorkerStatus()]);
      setVideos(vs); setWorker(w);
    } catch (err) { toast.error(err.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // keep the list fresh while anything is queued or being prepared
  const active = videos && videos.some(v => v.status === 'requested' || v.status === 'preparing');
  useEffect(() => {
    if (!active || reviewId) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [active, reviewId, load]);

  if (reviewId) return <VideoReview id={reviewId} onBack={() => { setReviewId(null); load(); }} onChanged={load} />;

  if (wizard) {
    return <VideoWizard onClose={() => { setWizard(false); load(); }}
                        onReview={(id) => { setWizard(false); setReviewId(id); }}
                        onChanged={() => { load(); setResultsKey(k => k + 1); }} />;
  }

  return (
    <div className="video-page">
      <div className="video-row"><h2>Videos</h2><WorkerBadge worker={worker} /></div>
      <div className="video-card wizard-promo">
        <div>
          <strong>New here? Use guided setup.</strong>
          <div className="video-meta">Add a student, add a video, and schedule its parts, one step at a time.</div>
        </div>
        <button type="button" className="btn-primary" onClick={() => setWizard(true)}>Guided setup</button>
      </div>
      <VideoRequestForm onRequested={load} />
      {videos === null && <div className="loading">Loading…</div>}
      {videos && videos.length === 0 && <div className="empty-state">No videos yet. Add one above.</div>}
      {videos && videos.length > 0 && (
        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {videos.map(v => <VideoCard key={v.id} v={v} onReview={setReviewId} onChanged={() => { load(); setResultsKey(k => k + 1); }} onAssigned={() => setResultsKey(k => k + 1)} />)}
        </ul>
      )}
      <AssignmentResults refreshKey={resultsKey} />
    </div>
  );
}
